import React, { useState } from 'react';
import styled from 'styled-components';
import { Card, Modal, Button } from 'antd';
import { Pie } from 'react-chartjs-2';
import colors from 'Styles/Colors';
import { EyeOutlined } from '@ant-design/icons';

const AddOnsCard = ({ data }) => {
  const [showModal, setShowModal] = useState(false);
  const addOns = data?.addOnsSummary || [];

  // Generate colors for the pie chart
  const generateColors = count => {
    const baseColors = ['#ff9f43', '#10AC84', '#2875c3', '#8395a7', '#576574', '#EE5253', '#ff6b6b', '#4834d4', '#dda0dd', '#98d8c8', '#f7dc6f', '#bb8fce'];

    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    return colors;
  };

  // Chart data
  const chartData = {
    labels: addOns.map(addon => addon.name),
    datasets: [
      {
        data: addOns.map(addon => addon.qty),
        backgroundColor: generateColors(addOns.length),
        borderWidth: 0,
        hoverOffset: 6
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 15,
        bottom: 15,
        left: 15,
        right: 15
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        position: 'nearest',
        callbacks: {
          label: function(context) {
            const addon = addOns[context.dataIndex];
            return `${context.label}: ${context.parsed} sold ($${addon.revenue.toFixed(2)})`;
          }
        }
      }
    }
  };

  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const totalAddOnsRevenue = addOns.reduce((sum, addon) => sum + (addon.revenue || 0), 0);
  const totalAddOnsSold = addOns.reduce((sum, addon) => sum + (addon.qty || 0), 0);

  const handleViewMore = () => {
    setShowModal(true);
  };

  const AddonModal = () => (
    <Modal
      title="Add-Ons Sales Details"
      open={showModal}
      onCancel={() => setShowModal(false)}
      footer={[
        <Button key="close" type="primary" onClick={() => setShowModal(false)}>
          Close
        </Button>
      ]}
      width={700}
      className="addon-modal">
      <AddonModalContent>
        <div className="modal-summary">
          <div className="summary-stats">
            <div className="summary-item">
              <div className="summary-number">{totalAddOnsSold}</div>
              <div className="summary-label">Total Items Sold</div>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-item">
              <div className="summary-number">{formatCurrency(totalAddOnsRevenue)}</div>
              <div className="summary-label">Total Revenue</div>
            </div>
          </div>
        </div>

        <div className="addon-list">
          <div className="addon-list-header">
            <div className="header-item">Add-On</div>
            <div className="header-item">Price</div>
            <div className="header-item">Qty Sold</div>
            <div className="header-item">Revenue</div>
          </div>
          {addOns.map((addon, index) => (
            <div key={addon.id || index} className="addon-row">
              <div className="addon-name-section">
                <div className="color-indicator" style={{ backgroundColor: generateColors(addOns.length)[index] }}></div>
                <div className="addon-details">
                  <div className="addon-name">{addon.name}</div>
                  <div className="addon-id">ID: {addon.id}</div>
                </div>
              </div>
              <div className="addon-price">{formatCurrency(addon.price)}</div>
              <div className="addon-quantity">{addon.qty}</div>
              <div className="addon-revenue">{formatCurrency(addon.revenue)}</div>
            </div>
          ))}
        </div>
      </AddonModalContent>
    </Modal>
  );

  return (
    <AddOnsCardContainer>
      <Card title="Add-Ons" className="dashboard-card">
        {addOns.length > 0 ? (
          <>
            <div className="chart-section">
              <div className="chart-wrapper">
                <Pie data={chartData} options={chartOptions} />
              </div>
              <div className="chart-stats">
                <div className="stat-item">
                  <div className="stat-number">{totalAddOnsSold}</div>
                  <div className="stat-label">Total Sold</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{formatCurrency(totalAddOnsRevenue)}</div>
                  <div className="stat-label">Revenue</div>
                </div>
              </div>
            </div>

            <div className="addons-list">
              {addOns.slice(0, 2).map((addon, index) => (
                <div key={addon.id || index} className="addon-item">
                  <div className="color-indicator" style={{ backgroundColor: generateColors(addOns.length)[index] }}></div>
                  <div className="addon-info">
                    <div className="addon-name">{addon.name}</div>
                    <div className="addon-revenue">{formatCurrency(addon.revenue)}</div>
                  </div>
                </div>
              ))}

              {addOns.length > 2 && (
                <div className="view-more-section">
                  <Button type="primary" size="small" icon={<EyeOutlined />} onClick={handleViewMore} className="view-more-btn">
                    View All Add-Ons ({addOns.length})
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="no-addons">
            <div className="no-addons-text">No add-ons sold</div>
          </div>
        )}
      </Card>

      <AddonModal />
    </AddOnsCardContainer>
  );
};

const AddOnsCardContainer = styled.div`
  flex: 1;
  min-width: 300px;

  .dashboard-card {
    height: 100%;
    overflow: visible;

    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
      color: ${colors.text.primary};
    }

    .ant-card-body {
      padding: 16px;
      overflow: visible;
    }
  }

  .chart-section {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
    padding: 10px;
    overflow: visible;
  }

  .chart-wrapper {
    width: 160px;
    height: 160px;
    flex-shrink: 0;
    overflow: visible;
    z-index: 10;
  }

  .chart-stats {
    flex: 1;

    .stat-item {
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }

      .stat-number {
        font-size: 24px;
        font-weight: 600;
        color: ${colors.text.primary};
        line-height: 1;
      }

      .stat-label {
        font-size: 12px;
        color: ${colors.text.secondary};
        text-transform: uppercase;
        margin-top: 4px;
      }
    }
  }

  .addons-list {
    border-top: 1px solid ${colors.border.primary};
    padding-top: 16px;

    .addon-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
      padding: 8px 0;

      &:last-child {
        margin-bottom: 0;
      }

      .color-indicator {
        width: 12px;
        height: 12px;
        border-radius: 2px;
        margin-right: 12px;
        flex-shrink: 0;
      }

      .addon-info {
        display: flex;
        align-items: center;
        flex: 1;

        .addon-name {
          font-size: 14px;
          font-weight: 500;
          color: ${colors.text.primary};
        }

        .addon-revenue {
          font-size: 14px;
          font-weight: 600;
          color: ${colors.text.primary};
          margin-left: auto;
        }
      }
    }

    .view-more-section {
      text-align: center;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid ${colors.border.primary}50;

      .view-more-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
      }
    }
  }

  .no-addons {
    text-align: center;
    padding: 40px 20px;

    .no-addons-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }

    .no-addons-text {
      font-size: 14px;
      color: ${colors.text.secondary};
    }
  }
`;

const AddonModalContent = styled.div`
  .modal-summary {
    margin-bottom: 32px;

    .summary-stats {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background: linear-gradient(135deg, ${colors.primary}08, ${colors.primary}03);
      border-radius: 12px;
      border: 1px solid ${colors.primary}20;

      .summary-item {
        text-align: center;

        .summary-number {
          font-size: 28px;
          font-weight: 700;
          color: ${colors.text.primary};
          line-height: 1;
          margin-bottom: 8px;
        }

        .summary-label {
          font-size: 12px;
          color: ${colors.text.secondary};
          text-transform: uppercase;
          font-weight: 500;
          letter-spacing: 0.5px;
        }
      }

      .summary-divider {
        width: 1px;
        height: 40px;
        background: ${colors.border.primary};
        margin: 0 40px;
      }
    }
  }

  .addon-list {
    .addon-list-header {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1.2fr;
      gap: 16px;
      padding: 12px 16px;
      background: ${colors.background.primary};
      border-radius: 8px 8px 0 0;
      border: 1px solid ${colors.border.primary};
      border-bottom: none;

      .header-item {
        font-size: 12px;
        font-weight: 600;
        color: ${colors.text.secondary};
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }

    .addon-row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1.2fr;
      gap: 16px;
      align-items: center;
      padding: 16px;
      border: 1px solid ${colors.border.primary};
      border-top: none;
      background: white;
      transition: background-color 0.2s ease;

      &:hover {
        background: ${colors.background.primary}30;
      }

      &:last-child {
        border-radius: 0 0 8px 8px;
      }

      .addon-name-section {
        display: flex;
        align-items: center;
        gap: 12px;

        .color-indicator {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .addon-details {
          .addon-name {
            font-size: 14px;
            font-weight: 600;
            color: ${colors.text.primary};
            margin-bottom: 2px;
          }

          .addon-id {
            font-size: 11px;
            color: ${colors.text.secondary};
          }
        }
      }

      .addon-price {
        font-size: 14px;
        font-weight: 500;
        color: ${colors.text.primary};
      }

      .addon-quantity {
        font-size: 14px;
        font-weight: 600;
        color: ${colors.text.primary};
        text-align: center;
      }

      .addon-revenue {
        font-size: 15px;
        font-weight: 700;
        color: ${colors.primary};
      }
    }
  }
`;

export default AddOnsCard;
